/**
 * Tier 4–7 creatures: Snail, Bug, Butterfly, Lizard — the "garden" stretch of the chain, redrawn
 * in code after the original merge pictures (assets/merge/v2/scaled/<id>.png): the same pose,
 * proportions, colours and expression, as clean glossy vector toys.
 *
 *   Snail      a cream jelly snail under a glossy amber spiral shell, two ball-tipped antennae
 *              and a bored face (heavy level lids over open red-brown eyes, a smirk).
 *   Bug        a lime caterpillar-beetle: a big round head with ball-tipped brown antennae, a
 *              rounded green back with a darker wing case, stubby brown feet.
 *   Butterfly  front on: a round yellow head, an orange segmented body, pink-to-orange upper
 *              wings and blue lower wings, all with plum and indigo outlines.
 *   Lizard     a lime gecko lying down: a long loaf of a body, yellow back spikes, a tail curled
 *              into a spiral, round haunches and a cream belly.
 *
 * Style, as in the originals: small simple faces (dark oval eyes with one white highlight, a
 * tiny smile, pink blush), every part outlined in a darker shade of its own colour, three-tone
 * cel shading lit from the upper left, glossy white highlight spots. No props, calm poses.
 *
 * Bubbles follow the original disc colours: Snail sunny yellow-orange, Bug lime, Butterfly a
 * pastel rainbow, Lizard lime-yellow, each with soft bokeh dots in the glass.
 *
 * Contract (creatures/index.js): { disc, discPattern?(c, r), draw(c, r, pose) }, centred on the
 * origin inside radius r (everything within 0.93 r). pose.mood: the kit moods; "blink" closes
 * only the eyes. Each creature carries its own outline (it is also drawn alone, pose.noDisc,
 * and rotated over an upright bubble in game). Deterministic.
 */
import { TAU, seededRandom, sparklePath } from "../toon-kit.js";
import { rgba, tone, candyPart, candyRope, smoothPath, ellipsePath } from "./candy-kit.js";

// ---------------------------------------------------------------------------------------
// Shared paint. Coordinates are in units of u = r × the creature's fit scale.

/** Outline weight: moderate and nearly even, like the originals' line art. */
var LINE = 0.03;
var SWELL_K = 0.5;
var RIM = 0.3;

/** A glossy part: coloured outline, shade / base / light, a soft rim light on the lit edge. */
function part(c, u, path, t, R, cx, cy, o) {
  var opt = o || {};
  candyPart(c, u, path, t, R * u, {
    line: opt.line != null ? opt.line : LINE,
    swell: opt.swell != null ? opt.swell : SWELL_K,
    cx: cx * u,
    cy: cy * u,
    lightK: opt.lightK || 0.66,
    rim: opt.rim != null ? opt.rim : RIM,
    inside: opt.inside,
  });
}

function smooth(pts, u) {
  return function (c) {
    smoothPath(c, pts, u);
  };
}

function oval(x, y, rx, ry, rot, u) {
  return ellipsePath(x * u, y * u, rx * u, ry * u, rot || 0);
}

/** Several sub-shapes as one (all wound clockwise, so the nonzero fill is their union). */
function union(paths) {
  return function (c) {
    for (var i = 0; i < paths.length; i++) paths[i](c);
  };
}

/** A white gloss spot. */
function gloss(c, u, x, y, rx, ry, rot, a) {
  c.fillStyle = "rgba(255,255,255," + (a != null ? a : 0.92) + ")";
  c.beginPath();
  c.ellipse(x * u, y * u, rx * u, ry * u, rot || 0, 0, TAU);
  c.fill();
}

/** Little round specks [x, y, size] in one colour. */
function specks(c, u, list, color) {
  c.fillStyle = color;
  for (var i = 0; i < list.length; i++) {
    c.beginPath();
    c.arc(list[i][0] * u, list[i][1] * u, list[i][2] * u, 0, TAU);
    c.fill();
  }
}

/** A stroked centre line (spirals, segment lines) in unit points. */
function strokePts(c, u, pts, color, w) {
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = color;
  c.lineWidth = w * u;
  c.beginPath();
  c.moveTo(pts[0][0] * u, pts[0][1] * u);
  for (var i = 1; i < pts.length; i++) c.lineTo(pts[i][0] * u, pts[i][1] * u);
  c.stroke();
  c.restore();
}

/** Points of a spiral round (cx, cy): radius r0 → r1 while the angle runs a0 → a1. */
function spiral(cx, cy, r0, r1, a0, a1, n) {
  var out = [];
  for (var i = 0; i <= n; i++) {
    var t = i / n, a = a0 + (a1 - a0) * t, rr = r0 + (r1 - r0) * t;
    out.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
  }
  return out;
}

/** A ball-tipped stalk: a rope from (x0, y0) bending through (qx, qy) to (x1, y1), in unit points. */
function stalk(c, u, x0, y0, qx, qy, x1, y1, core, t) {
  candyRope(c, function (cc) {
    cc.moveTo(x0 * u, y0 * u);
    cc.quadraticCurveTo(qx * u, qy * u, x1 * u, y1 * u);
  }, u, core, t);
}

// ---------------------------------------------------------------------------------------
// The face: small and simple, like the originals.

var EYE_DARK = "#24150e";
var MOUTH_INK = "#5a2615";
var MOUTH_FILL = "#86301f";
var TONGUE_FILL = "#ff8d8d";
var BLUSH = "#ff7676";

/** Moods → [eyes, mouth]; "blink" keeps the resting mouth. */
var FACE_MOODS = {
  smile: ["open", "smile"],
  grin: ["open", "grin"],
  happy: ["happy", "grin"],
  wow: ["wow", "o"],
  sleepy: ["sleepy", "small"],
  fierce: ["cross", "frown"],
  cool: ["lidded", "smirk"],
  wink: ["wink", "smile"],
  proud: ["happy", "smirk"],
};

/**
 * F (unit coordinates): eyes [[x, y] left, [x, y] right], e (eye half-width), ey (height
 * ratio), mouth [x, y, half-width], blush [[x, y], [x, y]], bw / bh (blush radii), bored
 * (the Snail's rest: level lids and a smirk), iris (bored eye colour).
 */
function gardenFace(c, u, F, pose) {
  var mood = pose && pose.mood;
  var rest = F.bored ? ["bored", "smirkFlip"] : ["open", "smile"];
  var m = FACE_MOODS[mood] || rest;
  if (mood === "blink") m = [F.bored ? "boredShut" : "shut", rest[1]];
  c.save();
  c.fillStyle = rgba(BLUSH, F.blushA || 0.6);
  for (var b = 0; b < 2; b++) {
    c.beginPath();
    c.ellipse(F.blush[b][0] * u, F.blush[b][1] * u, F.bw * u, F.bh * u, 0, 0, TAU);
    c.fill();
  }
  c.restore();
  faceMouth(c, u, F.mouth[0] * u, F.mouth[1] * u, F.mouth[2] * u, m[1]);
  for (var i = 0; i < 2; i++) {
    var side = i === 0 ? -1 : 1;
    var kind = m[0];
    if (kind === "wink") kind = side < 0 ? "open" : "happy";
    faceEye(c, u, F.eyes[i][0] * u, F.eyes[i][1] * u, F.e * u, F.ey || 1.12, kind, side, F);
  }
}

function faceEye(c, u, x, y, e, ey, kind, side, F) {
  var ry = e * ey;
  var lw = 0.024 * u;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = EYE_DARK;
  c.fillStyle = EYE_DARK;
  c.lineWidth = lw;
  if (kind === "shut") {
    c.beginPath();
    c.moveTo(x - e, y + ry * 0.05);
    c.quadraticCurveTo(x, y + ry * 0.85, x + e, y + ry * 0.05);
    c.stroke();
  } else if (kind === "happy") {
    c.beginPath();
    c.moveTo(x - e, y + ry * 0.35);
    c.quadraticCurveTo(x, y - ry * 0.75, x + e, y + ry * 0.35);
    c.stroke();
  } else if (kind === "cross") {
    // A brow slanting down toward the nose, the eye tucked under it.
    var ox = x + side * e * 1.2, oy = y - ry * 0.78;
    var ix = x - side * e * 1.12, iy = y - ry * 0.18;
    c.save();
    c.beginPath();
    c.moveTo(ox, oy);
    c.lineTo(ix, iy);
    c.lineTo(ix, y + ry * 2);
    c.lineTo(ox, y + ry * 2);
    c.closePath();
    c.clip();
    c.beginPath();
    c.ellipse(x, y, e, ry, 0, 0, TAU);
    c.fill();
    c.fillStyle = F.iris || "#8f2a16";
    c.beginPath();
    c.ellipse(x, y + ry * 0.32, e * 0.76, ry * 0.6, 0, 0, TAU);
    c.fill();
    c.fillStyle = rgba("#ff7a4a", 0.6);
    c.beginPath();
    c.ellipse(x, y + ry * 0.64, e * 0.46, ry * 0.24, 0, 0, TAU);
    c.fill();
    c.restore();
    c.fillStyle = "#ffffff";
    c.beginPath();
    c.arc(x - side * e * 0.04, y + ry * 0.0, e * 0.3, 0, TAU);
    c.fill();
    c.lineWidth = lw * 2;
    c.beginPath();
    c.moveTo(ox, oy - lw * 0.4);
    c.lineTo(ix, iy - lw * 0.4);
    c.stroke();
  } else if (kind === "bored" || kind === "boredShut") {
    // A heavy, dead-level lid across the top of the open eye (a slant either way reads cross
    // or sad). Shut, the same lid sits lower with nothing under it.
    var drop = kind === "boredShut" ? ry * 0.55 : 0;
    var lox = x + side * e * 1.15, loy = y - ry * 0.3 + drop;
    var lix = x - side * e * 1.15, liy = loy;
    if (kind === "bored") {
      c.save();
      c.beginPath();
      c.moveTo(lox, loy);
      c.lineTo(lix, liy);
      c.lineTo(lix, y + ry * 2);
      c.lineTo(lox, y + ry * 2);
      c.closePath();
      c.clip();
      c.beginPath();
      c.ellipse(x, y, e, ry, 0, 0, TAU);
      c.fill();
      c.fillStyle = F.iris || "#8f2a16";
      c.beginPath();
      c.ellipse(x, y + ry * 0.32, e * 0.76, ry * 0.6, 0, 0, TAU);
      c.fill();
      c.fillStyle = rgba("#ff7a4a", 0.6);
      c.beginPath();
      c.ellipse(x, y + ry * 0.64, e * 0.46, ry * 0.24, 0, 0, TAU);
      c.fill();
      c.fillStyle = "#ffffff";
      c.beginPath();
      c.arc(x - e * 0.3, y + ry * 0.02, e * 0.24, 0, TAU);
      c.fill();
      c.restore();
    }
    c.lineWidth = lw * 1.3;
    c.beginPath();
    c.moveTo(lox, loy - lw * 0.2);
    c.lineTo(lix, liy - lw * 0.2);
    c.stroke();
  } else {
    var k = kind === "wow" ? 1.22 : 1;
    var ex = e * k, eyy = ry * k;
    var cut = kind === "sleepy" ? y + eyy * 0.05 : y - eyy * 0.32;
    c.save();
    if (kind === "sleepy" || kind === "lidded") {
      c.beginPath();
      c.rect(x - ex * 2, cut, ex * 4, eyy * 3);
      c.clip();
    }
    c.beginPath();
    c.ellipse(x, y, ex, eyy, 0, 0, TAU);
    c.fill();
    c.fillStyle = "#ffffff";
    c.beginPath();
    c.arc(x - ex * 0.28, y - eyy * 0.34, ex * 0.38, 0, TAU);
    c.fill();
    c.beginPath();
    c.arc(x + ex * 0.32, y + eyy * 0.4, ex * 0.13, 0, TAU);
    c.fill();
    c.restore();
    if (kind === "sleepy" || kind === "lidded") {
      c.beginPath();
      c.moveTo(x - ex * 1.1, cut);
      c.lineTo(x + ex * 1.1, cut);
      c.stroke();
    }
  }
  c.restore();
}

function faceMouth(c, u, x, y, w, kind) {
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = MOUTH_INK;
  c.lineWidth = 0.022 * u;
  c.beginPath();
  if (kind === "smile") {
    c.moveTo(x - w, y - w * 0.15);
    c.quadraticCurveTo(x, y + w * 0.95, x + w, y - w * 0.15);
    c.stroke();
  } else if (kind === "frown") {
    c.moveTo(x - w, y + w * 0.4);
    c.quadraticCurveTo(x, y - w * 0.6, x + w, y + w * 0.4);
    c.stroke();
  } else if (kind === "smirk" || kind === "smirkFlip") {
    // One corner curled up: the right one, or the left when flipped.
    var d = kind === "smirkFlip" ? -1 : 1;
    c.moveTo(x - d * w, y + w * 0.05);
    c.quadraticCurveTo(x + d * w * 0.1, y + w * 0.75, x + d * w, y - w * 0.35);
    c.stroke();
  } else if (kind === "grin") {
    c.moveTo(x - w, y - w * 0.2);
    c.quadraticCurveTo(x, y, x + w, y - w * 0.2);
    c.bezierCurveTo(x + w * 0.9, y + w * 1.15, x - w * 0.9, y + w * 1.15, x - w, y - w * 0.2);
    c.closePath();
    c.fillStyle = MOUTH_FILL;
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = TONGUE_FILL;
    c.beginPath();
    c.ellipse(x, y + w * 0.9, w * 0.6, w * 0.42, 0, 0, TAU);
    c.fill();
    c.restore();
    c.stroke();
  } else {
    var k = kind === "o" ? 0.5 : 0.32;
    c.ellipse(x, y + w * 0.25, w * k, w * k * 1.2, 0, 0, TAU);
    c.fillStyle = MOUTH_FILL;
    c.fill();
    c.stroke();
  }
  c.restore();
}

// ---------------------------------------------------------------------------------------
// Glass patterns: soft bokeh dots like the originals' glittering backgrounds.

function bokeh(c, r, seed, n, color, a0, a1) {
  var rnd = seededRandom(seed);
  for (var i = 0; i < n; i++) {
    var ang = rnd() * TAU, d = Math.sqrt(rnd()) * 0.84;
    var s = 0.012 + rnd() * rnd() * 0.04;
    c.fillStyle = rgba(color, a0 + rnd() * (a1 - a0));
    c.beginPath();
    c.arc(Math.cos(ang) * d * r, Math.sin(ang) * d * r, s * r, 0, TAU);
    c.fill();
  }
}

// ---------------------------------------------------------------------------------------
// Tier 4 — Snail: a cream jelly snail facing right under a glossy amber spiral shell, two
// ball-tipped antennae growing out of the head, a bored face. As in the original, the shell's
// right edge and the head are one line: it rises straight from the spiral up the shell's side,
// then on over the head as one arch (peaking between the antennae) down to the foot; the shell's
// top, one smooth curve fullest at its top left, comes down to meet that line. Shell and head
// share one ink so the line reads unbroken. The spiral winds inward counter-clockwise, as in the original.

var SN_K = 0.96;
/** One ink for the shell and the head, so the arch where they meet reads as one line. */
var SN_INK = "#ae4208";
var SN_SHELL = tone(SN_INK, "#e65a04", "#f7800c", "#ffb238", "#ee6a06");
var SN_BODY = tone(SN_INK, "#f3be55", "#ffe69a", "#fff7d8", "#f7cf78");
var SN_KNOB = tone("#cc560a", "#f29216", "#ffb42a", "#ffdc68", "#f8a420");
var SN_SPIRAL_INK = "#ad3304";

/**
 * The arch: the shell's right edge, a straight line rising from the spiral (to the corner where
 * the shell's top comes down onto it, SN_ARCH[SN_JOIN], and a little past it), then on over the
 * head, peaking between the antennae, down the head's right side.
 */
var SN_ARCH = [
  [-0.2, 0.28], [-0.131, 0.183], [-0.02, 0.012], [0.091, -0.159], [0.202, -0.33],
  [0.237, -0.385], [0.3, -0.425], [0.385, -0.44], [0.48, -0.42], [0.58, -0.35],
  [0.665, -0.235], [0.715, -0.08], [0.75, 0.12], [0.76, 0.35],
];
var SN_JOIN = 4;
/**
 * How far the head's own outline sits inside the arch, per arch point: along the shell's edge
 * and just past the corner, the shell's ink lies on the head's side of the arch, so the head's
 * outline is pulled in by a line's width there and its ink lands on the same band, carrying the
 * line on unbroken; over the top the offset eases back to nothing.
 */
var SN_ARCH_INSET = [0.036, 0.036, 0.036, 0.036, 0.036, 0.04, 0.03, 0.01];

/** The arch with each point moved toward the head (the left normal of its direction) by SN_ARCH_INSET. */
function snailHeadArch() {
  var n = SN_ARCH.length, out = [];
  for (var i = 0; i < n; i++) {
    var a = SN_ARCH[Math.max(0, i - 1)], b = SN_ARCH[Math.min(n - 1, i + 1)];
    var dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy) || 1, k = SN_ARCH_INSET[i] || 0;
    out.push([SN_ARCH[i][0] - (dy / d) * k, SN_ARCH[i][1] + (dx / d) * k]);
  }
  return out;
}

/** Head and foot (one jelly shape): the arch, then the long flat foot and the tail under the shell. */
var SN_BODY_PTS = snailHeadArch().slice(1).concat([
  [0.74, 0.47], [0.62, 0.575], [0.3, 0.6], [-0.25, 0.6], [-0.62, 0.58], [-0.78, 0.5],
  [-0.72, 0.41], [-0.45, 0.36], [-0.26, 0.24],
]);
/**
 * Antenna stalks [base x, base y, top x, top y, base half-width, top half-width]: slim cream
 * tubes in a slight V, their bases sunk into the head's top so they grow out of it.
 */
var SN_STALKS = [[0.362, -0.33, 0.296, -0.64, 0.03, 0.022], [0.492, -0.33, 0.566, -0.63, 0.03, 0.022]];
/**
 * The shell's top, one smooth run from the corner on the arch: up and over to the round top
 * left and down the left side as an even oval arc (points sampled on one ellipse so the curve
 * has no lumps), then along the bottom to where its right edge (the arch) starts.
 */
var SN_SHELL_TOP = [
  [0.202, -0.33], [0.062, -0.431], [-0.103, -0.495], [-0.279, -0.519], [-0.452, -0.5], [-0.606, -0.44],
  [-0.731, -0.343], [-0.815, -0.218], [-0.851, -0.075], [-0.837, 0.075], [-0.774, 0.219], [-0.667, 0.345],
  [-0.5, 0.435], [-0.32, 0.4], [-0.2, 0.28],
];
/**
 * The spiral's centre line, branching off the shell's right side where the lip meets it and
 * winding inward counter-clockwise (over the top, down the left, along the bottom) to a curl.
 */
var SN_SPIRAL_PTS = [
  [-0.118, 0.22], [-0.16, 0.07], [-0.187, -0.03], [-0.222, -0.11], [-0.28, -0.18], [-0.355, -0.225],
  [-0.44, -0.238], [-0.52, -0.222], [-0.588, -0.18], [-0.635, -0.112], [-0.655, -0.03], [-0.645, 0.055],
  [-0.603, 0.13], [-0.537, 0.182], [-0.46, 0.198], [-0.39, 0.178], [-0.342, 0.13], [-0.32, 0.062],
  [-0.326, 0], [-0.352, -0.042], [-0.4, -0.062], [-0.435, -0.063], [-0.468, -0.055], [-0.494, -0.036],
  [-0.51, -0.009], [-0.512, 0.021], [-0.503, 0.049], [-0.483, 0.069], [-0.458, 0.078], [-0.432, 0.077],
  [-0.41, 0.065], [-0.395, 0.046],
];
/** Antenna knobs [x, y, radius], sitting on the stalk tops. */
var SN_KNOBS = [[0.286, -0.67, 0.072], [0.568, -0.66, 0.07]];
var SN_FACE = {
  eyes: [[0.25, 0.03], [0.555, 0.03]],
  e: 0.084,
  ey: 0.9,
  mouth: [0.41, 0.14, 0.052],
  blush: [[0.16, 0.14], [0.66, 0.14]],
  bw: 0.055,
  bh: 0.038,
  bored: true,
  iris: "#b52c18",
};

/** Points along an open Catmull-Rom run through unit points (ends clamped), `per` per span. */
function snailSpline(pts, per) {
  var out = [], n = pts.length;
  for (var i = 0; i < n - 1; i++) {
    var p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(n - 1, i + 2)];
    var c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    var c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    for (var k = 0; k < per; k++) {
      var t = k / per, s = 1 - t;
      out.push([
        s * s * s * p1[0] + 3 * s * s * t * c1[0] + 3 * s * t * t * c2[0] + t * t * t * p2[0],
        s * s * s * p1[1] + 3 * s * s * t * c1[1] + 3 * s * t * t * c2[1] + t * t * t * p2[1],
      ]);
    }
  }
  out.push(pts[n - 1]);
  return out;
}

/** A closed path along unit points (the corner where the run's ends meet stays sharp). */
function snailPath(pts, u) {
  return function (c) {
    c.moveTo(pts[0][0] * u, pts[0][1] * u);
    for (var i = 1; i < pts.length; i++) c.lineTo(pts[i][0] * u, pts[i][1] * u);
    c.closePath();
  };
}

/** One antenna stalk's tube (unit-point spec, see SN_STALKS). */
function snailStalkPath(s, u) {
  var dx = s[2] - s[0], dy = s[3] - s[1], d = Math.hypot(dx, dy) || 1;
  var nx = -dy / d, ny = dx / d;
  return function (c) {
    c.moveTo((s[0] + nx * s[4]) * u, (s[1] + ny * s[4]) * u);
    c.lineTo((s[2] + nx * s[5]) * u, (s[3] + ny * s[5]) * u);
    c.lineTo((s[2] - nx * s[5]) * u, (s[3] - ny * s[5]) * u);
    c.lineTo((s[0] - nx * s[4]) * u, (s[1] - ny * s[4]) * u);
    c.closePath();
  };
}

/**
 * The stalks. "back": inked tubes, drawn before the head so the head's top hides their feet.
 * "front": their cream fill again over the head's top edge, fading out inside the head, so
 * each stalk grows out of the head with no outline across its foot.
 */
function snailStalks(c, u, pass) {
  for (var i = 0; i < SN_STALKS.length; i++) {
    var path = snailStalkPath(SN_STALKS[i], u);
    c.save();
    c.beginPath();
    path(c);
    if (pass === "back") {
      c.lineJoin = "round";
      c.strokeStyle = SN_BODY.ink;
      c.lineWidth = LINE * 2 * u;
      c.stroke();
      c.fillStyle = SN_BODY.light;
      c.fill();
    } else {
      var g = c.createLinearGradient(0, -0.47 * u, 0, -0.35 * u);
      g.addColorStop(0, rgba(SN_BODY.light, 1));
      g.addColorStop(0.55, rgba(SN_BODY.light, 1));
      g.addColorStop(1, rgba(SN_BODY.light, 0));
      c.fillStyle = g;
      c.clip();
      c.fillRect(-u, -0.47 * u, 2 * u, 0.2 * u);
    }
    c.restore();
  }
}

/** A stroke along unit points that thins from w0 to w1 (the spiral tapering into its curl). */
function snailTaper(c, u, pts, color, w0, w1) {
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = color;
  for (var i = 1; i < pts.length; i++) {
    c.lineWidth = (w0 + (w1 - w0) * (i / (pts.length - 1))) * u;
    c.beginPath();
    c.moveTo(pts[i - 1][0] * u, pts[i - 1][1] * u);
    c.lineTo(pts[i][0] * u, pts[i][1] * u);
    c.stroke();
  }
  c.restore();
}

/**
 * The shell's outline: its top run, then the arch up to the corner. Splined as one run through
 * the arch's next point too and cut at the corner, so its right edge follows the head's arch
 * exactly; only the corner where the top comes down onto the arch stays sharp.
 */
var SN_SHELL_OUTLINE = (function () {
  var run = SN_SHELL_TOP.concat(SN_ARCH.slice(1, SN_JOIN + 2));
  var pts = snailSpline(run, 8);
  return pts.slice(0, (run.length - 2) * 8 + 1);
})();
var SN_SPIRAL = snailSpline(SN_SPIRAL_PTS, 6);

export var snail = {
  disc: { base: "#ffd83c", light: "#fff8b0", shade: "#ffb21e", ink: "#e48a0a" },
  discPattern: function (c, r) {
    bokeh(c, r, 41, 30, "#fff7c4", 0.25, 0.6);
  },
  draw: function (c, r, pose) {
    var u = r * SN_K;

    // The stalks behind, the arched head on its long flat foot, then the stalks' feet over the
    // head's top edge so they grow out of it.
    snailStalks(c, u, "back");
    part(c, u, smooth(SN_BODY_PTS, u), SN_BODY, 0.55, 0.3, 0.15, {
      inside: function (cc) {
        // Amber deepening down into the foot, then the sole's light lip along it.
        var g = cc.createLinearGradient(0, 0.25 * u, 0, 0.62 * u);
        g.addColorStop(0, "rgba(250,170,50,0)");
        g.addColorStop(1, "rgba(246,160,40,0.5)");
        cc.fillStyle = g;
        cc.fillRect(-u, 0.25 * u, 2 * u, 0.4 * u);
        specks(cc, u, [
          [-0.08, 0.3, 0.014], [0.02, 0.24, 0.011], [-0.12, 0.42, 0.012], [0.12, 0.4, 0.01], [0.3, 0.44, 0.012],
        ], "rgba(255,238,170,0.8)");
        strokePts(cc, u, [[-0.7, 0.47], [-0.3, 0.495], [0.2, 0.51], [0.66, 0.5]], "rgba(255,250,226,0.85)", 0.024);
      },
    });
    snailStalks(c, u, "front");
    gloss(c, u, 0.2, -0.2, 0.065, 0.032, -0.4);
    gloss(c, u, 0.4, -0.275, 0.02, 0.016, 0);

    SN_KNOBS.forEach(function (k) {
      part(c, u, oval(k[0], k[1], k[2], k[2] * 0.96, 0, u), SN_KNOB, k[2], k[0], k[1], { lightK: 0.6 });
      gloss(c, u, k[0] - k[2] * 0.2, k[1] - k[2] * 0.42, k[2] * 0.36, k[2] * 0.24, -0.25);
    });

    candyPart(c, u, snailPath(SN_SHELL_OUTLINE, u), SN_SHELL, 0.5 * u, {
      line: LINE,
      swell: SWELL_K,
      rim: RIM,
      light: function (cc) {
        // A lit band along the top of the outer whorl; the sides stay deep orange.
        oval(-0.3, -0.375, 0.48, 0.12, 0.04, u)(cc);
      },
      inside: function (cc) {
        // A warm yellow glow round the coil, as in the original.
        var g = cc.createRadialGradient(-0.45 * u, 0, 0, -0.45 * u, 0, 0.34 * u);
        g.addColorStop(0, "rgba(255,230,84,1)");
        g.addColorStop(0.5, "rgba(255,214,62,0.92)");
        g.addColorStop(0.8, "rgba(255,180,40,0.45)");
        g.addColorStop(1, "rgba(255,150,20,0)");
        cc.fillStyle = g;
        cc.fillRect(-u, -0.5 * u, u, u);
        specks(cc, u, [
          [-0.72, 0.1, 0.018], [-0.66, 0.22, 0.016], [-0.55, 0.3, 0.014], [-0.4, 0.31, 0.016], [-0.3, 0.27, 0.012],
          [-0.76, -0.04, 0.012], [-0.24, 0.18, 0.011], [-0.62, 0.3, 0.01],
        ], "rgba(255,226,120,0.85)");
        snailTaper(cc, u, SN_SPIRAL, SN_SPIRAL_INK, 0.046, 0.039);
      },
    });
    gloss(c, u, -0.513, -0.283, 0.066, 0.036, -0.45);
    gloss(c, u, -0.313, -0.4, 0.05, 0.024, -0.2);
    gloss(c, u, -0.663, -0.208, 0.022, 0.034, 0.5);
    gloss(c, u, -0.17, -0.38, 0.022, 0.018, 0, 0.85);

    gardenFace(c, u, SN_FACE, pose);
  },
};

// ---------------------------------------------------------------------------------------
// Tier 5 — Bug: a jewel beetle facing left (shaped after the original, coloured after a real
// metallic blue beetle), layered as in the original: a long caramel-brown body underneath on
// dark blue bean feet, two glossy dark blue shell parts on its back (the dome behind, the wing
// case in front of it, nearly the same blue), the big round brown head in front, and two thin
// brown antennae standing on the head's top edge (drawn over it) to blue ball tips.

var BG_K = 0.97;
var BG_INK = "#4a2a12";
var BG_SHELL_INK = "#101e52";
var BG_HEAD = tone(BG_INK, "#b07444", "#cc915a", "#ecbe88", "#bb7e4c");
var BG_BODY = tone(BG_INK, "#8e5a30", "#ae7646", "#cf9a66", "#9c6538");
/** Both shell halves: one deep metallic blue, the dome a touch lighter so the two still read apart. */
var BG_DOME = tone(BG_SHELL_INK, "#22489e", "#3264c6", "#6c9cf2", "#2a56b2");
var BG_CASE = tone(BG_SHELL_INK, "#1c3c8e", "#2a56b8", "#6294ee", "#2248a2");
var BG_KNOB = tone(BG_SHELL_INK, "#22489e", "#3466c8", "#7aa8f4", "#2a56b2");
var BG_FOOT = tone("#0e1636", "#1c2a60", "#2a3e84", "#4e68b4", "#223474");
/** The far foot, in the body's shadow: much darker, like the original's second foot. */
var BG_FAR = tone("#080c22", "#121c42", "#1a2856", "#2e4278", "#16224a");
var BG_STALK = { ink: "#3a200c", base: "#7a4a22", light: "#b07a46" };

/** The head: a wide round loaf, a little fuller toward its base than an ellipse. */
var BG_HEAD_PTS = [
  [-0.27, -0.353], [-0.105, -0.315], [0.03, -0.235], [0.133, -0.125], [0.194, -0.012], [0.205, 0.075],
  [0.194, 0.19], [0.143, 0.298], [0.062, 0.37], [-0.055, 0.412], [-0.27, 0.433], [-0.475, 0.405],
  [-0.6, 0.35], [-0.69, 0.255], [-0.743, 0.075], [-0.714, -0.093], [-0.642, -0.215], [-0.518, -0.297],
  [-0.395, -0.338],
];
/** The body underneath: its front end hidden behind the head, leaving the chin at the front foot. */
var BG_BODY_PTS = [
  [-0.6, 0.32], [-0.53, 0.418], [-0.4, 0.478], [-0.25, 0.524], [0.0, 0.548], [0.2, 0.547],
  [0.4, 0.532], [0.57, 0.493], [0.69, 0.44], [0.775, 0.355], [0.822, 0.25], [0.838, 0.12],
  [0.79, 0.0], [0.5, -0.06], [0.0, -0.02], [-0.4, 0.08], [-0.52, 0.22],
];
/** The rear shell part: a light dome showing above the wing case, its front under the head. */
var BG_DOME_PTS = [
  [-0.2, -0.2], [-0.085, -0.325], [0.04, -0.39], [0.17, -0.425], [0.32, -0.44], [0.46, -0.42],
  [0.56, -0.37], [0.625, -0.3], [0.66, -0.2], [0.6, -0.05], [0.3, 0.02], [0.0, 0.02],
];
/** The front shell part: the darker wing case, an egg leaning back, its front under the head. */
var BG_CASE_PTS = [
  [0.02, -0.215], [0.1, -0.268], [0.235, -0.31], [0.41, -0.322], [0.525, -0.308], [0.638, -0.256],
  [0.736, -0.17], [0.802, -0.075], [0.84, 0.035], [0.838, 0.138], [0.81, 0.218], [0.743, 0.275],
  [0.635, 0.313], [0.5, 0.322], [0.36, 0.304], [0.25, 0.255], [0.12, 0.165], [0.03, 0.02],
];
/**
 * Feet [x, y, rx, ry, tilt]. Back pass (under the body): the far one, then the front one lying on
 * its side, tucked into the seam where the body leaves the chin. Then the four near ones.
 */
var BG_FAR_FOOT = [-0.358, 0.495, 0.047, 0.047, 0.3];
var BG_FRONT_FOOT = [-0.538, 0.462, 0.05, 0.064, 1.15];
var BG_FEET = [
  [0.03, 0.535, 0.046, 0.073, -0.12], [0.272, 0.522, 0.049, 0.073, -0.3],
  [0.5, 0.502, 0.051, 0.072, -0.36], [0.688, 0.47, 0.047, 0.062, -0.5],
];
var BG_FACE = {
  eyes: [[-0.545, -0.008], [-0.115, 0.002]],
  e: 0.071,
  ey: 1.12,
  mouth: [-0.343, 0.048, 0.08],
  blush: [[-0.652, 0.085], [0.01, 0.11]],
  bw: 0.056,
  bh: 0.046,
};

/** A bean foot [x, y, rx, ry, tilt]: an egg, narrower at its top where it leaves the body. */
function bugFoot(c, u, f, t) {
  var pts = [], ct = Math.cos(f[4]), st = Math.sin(f[4]);
  for (var i = 0; i < 12; i++) {
    var a = (i / 12) * TAU, s = Math.sin(a);
    var x = Math.cos(a) * f[2] * (1 + 0.16 * s), y = s * f[3];
    pts.push([f[0] + x * ct - y * st, f[1] + x * st + y * ct]);
  }
  part(c, u, smooth(pts, u), t || BG_FOOT, 0.07, f[0], f[1], { line: 0.024, swell: 0.4, lightK: 0.5, rim: 0.15 });
}

/**
 * A flat antenna, like the original's: a slim brown band from its foot (x0, y0) bending through
 * (qx, qy) to (x1, y1), w0 → w1 wide with round ends, a thin outline and a light stripe.
 */
function bugStalk(c, u, x0, y0, qx, qy, x1, y1, w0, w1) {
  var n = 14, lft = [], rgt = [], a0 = 0, a1 = 0;
  for (var i = 0; i <= n; i++) {
    var t = i / n, s = 1 - t;
    var x = s * s * x0 + 2 * s * t * qx + t * t * x1, y = s * s * y0 + 2 * s * t * qy + t * t * y1;
    var dx = 2 * s * (qx - x0) + 2 * t * (x1 - qx), dy = 2 * s * (qy - y0) + 2 * t * (y1 - qy);
    var a = Math.atan2(dy, dx), w = (w0 + (w1 - w0) * t) / 2;
    lft.push([x - Math.sin(a) * w, y + Math.cos(a) * w]);
    rgt.push([x + Math.sin(a) * w, y - Math.cos(a) * w]);
    if (i === 0) a0 = a;
    a1 = a;
  }
  var band = function (cc) {
    cc.moveTo(lft[0][0] * u, lft[0][1] * u);
    for (var j = 1; j <= n; j++) cc.lineTo(lft[j][0] * u, lft[j][1] * u);
    cc.arc(x1 * u, y1 * u, (w1 / 2) * u, a1 + Math.PI / 2, a1 - Math.PI / 2, true);
    for (var k = n; k >= 0; k--) cc.lineTo(rgt[k][0] * u, rgt[k][1] * u);
    cc.arc(x0 * u, y0 * u, (w0 / 2) * u, a0 - Math.PI / 2, a0 - Math.PI * 1.5, true);
    cc.closePath();
  };
  c.save();
  c.lineJoin = "round";
  c.strokeStyle = BG_STALK.ink;
  c.fillStyle = BG_STALK.ink;
  c.lineWidth = 0.022 * u;
  c.beginPath();
  band(c);
  c.stroke();
  c.fillStyle = BG_STALK.base;
  c.fill();
  c.clip();
  // The lit (left) edge catches a pale stripe.
  c.strokeStyle = rgba(BG_STALK.light, 0.9);
  c.lineCap = "round";
  c.lineWidth = w1 * 0.36 * u;
  c.beginPath();
  c.moveTo((x0 - w0 * 0.22) * u, (y0 - w0 * 0.3) * u);
  c.quadraticCurveTo((qx - w0 * 0.24) * u, qy * u, (x1 - w1 * 0.24) * u, y1 * u);
  c.stroke();
  c.restore();
}

export var bug = {
  // A warm coral bubble: no other tier uses red, and the blue shell stands out on it.
  disc: { base: "#ff7f6e", light: "#ffdcc6", shade: "#ec4f52", ink: "#c03842" },
  discPattern: function (c, r) {
    bokeh(c, r, 52, 30, "#fff0e2", 0.25, 0.6);
  },
  draw: function (c, r, pose) {
    var u = r * BG_K;
    bugFoot(c, u, BG_FAR_FOOT, BG_FAR);
    bugFoot(c, u, BG_FRONT_FOOT);

    // The body underneath, shaded where the head and the wing case overhang it.
    part(c, u, smooth(BG_BODY_PTS, u), BG_BODY, 0.24, 0.1, 0.3, {
      lightK: 0.7,
      inside: function (cc) {
        cc.save();
        cc.translate(0.012 * u, 0.045 * u);
        cc.beginPath();
        smoothPath(cc, BG_HEAD_PTS, u);
        smoothPath(cc, BG_CASE_PTS, u);
        cc.fillStyle = rgba(BG_BODY.shade, 0.75);
        cc.fill();
        cc.restore();
      },
    });

    // Two shell parts on the back: the light dome behind, the darker wing case over it.
    part(c, u, smooth(BG_DOME_PTS, u), BG_DOME, 0.4, 0.3, -0.2, {
      lightK: 0.72,
      inside: function (cc) {
        specks(cc, u, [
          [0.3, -0.36, 0.016], [0.42, -0.37, 0.012], [0.52, -0.33, 0.011], [0.2, -0.375, 0.01],
        ], "rgba(190,220,255,0.85)");
      },
    });
    gloss(c, u, 0.1, -0.375, 0.045, 0.02, -0.35, 0.85);

    part(c, u, smooth(BG_CASE_PTS, u), BG_CASE, 0.42, 0.45, 0.0, {
      lightK: 0.6,
      light: function (cc) {
        cc.ellipse(0.34 * u, -0.12 * u, 0.5 * u, 0.22 * u, -0.6, 0, TAU);
      },
      inside: function (cc) {
        specks(cc, u, [
          [0.44, -0.07, 0.03], [0.64, -0.12, 0.026], [0.28, -0.13, 0.018], [0.37, 0.03, 0.02],
          [0.54, 0.14, 0.02], [0.66, 0.15, 0.017], [0.5, -0.22, 0.014], [0.71, -0.03, 0.014],
          [0.6, 0.01, 0.013], [0.42, 0.2, 0.012], [0.74, 0.07, 0.011],
        ], "rgba(160,200,255,0.7)");
        // The light lip along the case's lower edge.
        cc.strokeStyle = "rgba(96,148,238,0.8)";
        cc.lineWidth = 0.016 * u;
        cc.lineCap = "round";
        cc.beginPath();
        cc.moveTo(0.27 * u, 0.215 * u);
        cc.bezierCurveTo(0.42 * u, 0.3 * u, 0.68 * u, 0.28 * u, 0.785 * u, 0.15 * u);
        cc.stroke();
      },
    });
    gloss(c, u, 0.3, -0.235, 0.05, 0.026, -0.3);
    gloss(c, u, 0.4, -0.26, 0.016, 0.012, 0, 0.85);

    for (var i = 0; i < BG_FEET.length; i++) bugFoot(c, u, BG_FEET[i]);

    part(c, u, smooth(BG_HEAD_PTS, u), BG_HEAD, 0.46, -0.27, 0.04, { lightK: 0.66 });
    gloss(c, u, -0.52, -0.225, 0.06, 0.028, -0.4);
    gloss(c, u, -0.445, -0.265, 0.017, 0.014, 0);

    // Antennae stand on the head's top edge, in front of it, like the original's.
    bugStalk(c, u, -0.418, -0.322, -0.438, -0.45, -0.5, -0.565, 0.036, 0.03);
    bugStalk(c, u, -0.13, -0.302, -0.065, -0.43, -0.072, -0.535, 0.036, 0.03);
    [[-0.545, -0.612, 0.078], [-0.064, -0.606, 0.08]].forEach(function (k) {
      part(c, u, oval(k[0], k[1], k[2], k[2] * 0.95, 0, u), BG_KNOB, 0.075, k[0], k[1], { lightK: 0.55 });
      gloss(c, u, k[0] - 0.022, k[1] - 0.026, 0.03, 0.018, -0.3);
    });

    gardenFace(c, u, BG_FACE, pose);
  },
};

// ---------------------------------------------------------------------------------------
// Tier 6 — Butterfly: front on, wings spread — big pink-to-orange upper wings lying over the
// blue lower wings, an orange segmented body and a round yellow head with two thin ball-tipped
// antennae. Traced from the original: the upper wings reach out to a notch on the outer edge,
// the lower wings are round lobes with a pale inner band, fine veins and a glassy spot.

var BF_K = 0.87;
/**
 * The whole butterfly sits a touch right and down of the original's spot: its upper wing tips
 * reach almost to the original's disc edge, and this keeps them inside the bubble (≤ 0.94 r).
 */
var BF_AT = [0.01, 0.035];
var BF_UPPER = tone("#9c2268", "#ee509e", "#ff6cb4", "#ffa0cf", "#f560aa");
var BF_LOWER = tone("#33268a", "#4176ea", "#5c9eff", "#9acbff", "#4c8af4");
var BF_BODY = tone("#8a300c", "#ea731a", "#ffa02e", "#ffc860", "#f78a22");
var BF_HEAD = tone("#a8570e", "#f6ad22", "#ffd43b", "#fff07e", "#fbc02e");
var BF_KNOB = tone("#8a3a0c", "#f2a020", "#ffc53a", "#ffe58a", "#f8b22c");
/** The antenna stalks: flat brown lines like the original's, not shaded ropes. */
var BF_STALK_INK = "#7a2e0c";
var BF_STALK_CORE = "#c0601c";

/**
 * Wing outlines (unit points, the ink centre line). [x, y, 1] marks a sharp corner: the small
 * notch where the original's forewing tip meets its lower edge.
 */
var BF_UPPER_L = [
  [-0.1, -0.2], [-0.2, -0.37], [-0.31, -0.53], [-0.43, -0.645], [-0.555, -0.725], [-0.672, -0.752],
  [-0.762, -0.738], [-0.818, -0.688], [-0.852, -0.605], [-0.865, -0.5], [-0.845, -0.39], [-0.8, -0.3],
  [-0.765, -0.255], [-0.745, -0.225, 1], [-0.757, -0.17], [-0.757, -0.09], [-0.72, -0.03],
  [-0.645, 0.03], [-0.55, 0.07], [-0.44, 0.09], [-0.33, 0.08], [-0.2, 0.03],
];
var BF_UPPER_R = [
  [0.1, -0.2], [0.2, -0.34], [0.29, -0.4], [0.415, -0.48], [0.54, -0.545], [0.665, -0.59],
  [0.79, -0.61], [0.874, -0.57], [0.912, -0.488], [0.915, -0.38], [0.885, -0.26], [0.825, -0.15],
  [0.775, -0.095], [0.75, -0.065, 1], [0.748, -0.01], [0.73, 0.06], [0.68, 0.12], [0.59, 0.18],
  [0.49, 0.21], [0.39, 0.215], [0.3, 0.2], [0.2, 0.15], [0.08, 0.05],
];
var BF_LOWER_L = [
  [-0.26, 0.13], [-0.45, 0.14], [-0.64, 0.15], [-0.715, 0.21], [-0.735, 0.31], [-0.72, 0.41],
  [-0.665, 0.48], [-0.585, 0.53], [-0.49, 0.55], [-0.39, 0.54], [-0.31, 0.5], [-0.24, 0.42],
  [-0.2, 0.25],
];
var BF_LOWER_R = [
  [0.1, 0.16], [0.3, 0.17], [0.5, 0.21], [0.57, 0.3], [0.6, 0.41], [0.585, 0.51], [0.53, 0.6],
  [0.44, 0.66], [0.34, 0.675], [0.22, 0.665], [0.12, 0.62], [0.04, 0.54], [0.04, 0.3],
];

/**
 * Per wing: centre (shading), root (where the veins and the root colour start), veins
 * [x0, y0, qx, qy, x1, y1], the big glassy spot [x, y, rx, ry, rot], and gloss dots [x, y, r].
 */
var BF_WINGS = {
  upperL: {
    pts: BF_UPPER_L, c: [-0.55, -0.35], root: [-0.18, -0.12],
    veins: [
      [-0.24, -0.3, -0.42, -0.5, -0.6, -0.64], [-0.24, -0.22, -0.52, -0.4, -0.8, -0.44],
      [-0.24, -0.14, -0.5, -0.22, -0.74, -0.2], [-0.24, -0.06, -0.45, -0.03, -0.64, 0.0],
    ],
    spot: [-0.62, -0.6, 0.095, 0.075, -0.4],
    dots: [
      [-0.51, -0.64, 0.015], [-0.74, -0.48, 0.027], [-0.41, -0.49, 0.03], [-0.35, -0.43, 0.018],
      [-0.665, -0.31, 0.02], [-0.64, -0.245, 0.012], [-0.6, -0.12, 0.014],
    ],
  },
  upperR: {
    pts: BF_UPPER_R, c: [0.55, -0.25], root: [0.15, -0.1],
    veins: [
      [0.2, -0.3, 0.38, -0.44, 0.58, -0.52], [0.2, -0.22, 0.5, -0.34, 0.84, -0.36],
      [0.2, -0.14, 0.5, -0.16, 0.78, -0.13], [0.2, -0.05, 0.42, 0.04, 0.64, 0.1],
    ],
    spot: [0.7, -0.46, 0.105, 0.075, -0.25],
    dots: [
      [0.467, -0.383, 0.04], [0.587, -0.516, 0.012], [0.808, -0.327, 0.034], [0.404, -0.34, 0.018],
      [0.713, -0.156, 0.022], [0.657, -0.106, 0.012], [0.543, -0.238, 0.012],
    ],
  },
  lowerL: {
    pts: BF_LOWER_L, c: [-0.5, 0.34], root: [-0.26, 0.15],
    veins: [
      [-0.27, 0.16, -0.36, 0.2, -0.46, 0.3], [-0.27, 0.16, -0.46, 0.17, -0.62, 0.22],
      [-0.27, 0.18, -0.31, 0.32, -0.4, 0.46],
    ],
    spot: [-0.52, 0.335, 0.088, 0.085, 0],
    dots: [[-0.578, 0.222, 0.02], [-0.495, 0.191, 0.014], [-0.62, 0.42, 0.01]],
    soft: [-0.4, 0.44, 0.045, 0.028, -0.6],
  },
  lowerR: {
    pts: BF_LOWER_R, c: [0.33, 0.44], root: [0.12, 0.18],
    veins: [
      [0.13, 0.19, 0.2, 0.28, 0.3, 0.36], [0.14, 0.19, 0.3, 0.22, 0.44, 0.29],
      [0.12, 0.2, 0.11, 0.32, 0.15, 0.46],
    ],
    spot: [0.355, 0.456, 0.09, 0.087, 0],
    dots: [[0.443, 0.342, 0.024], [0.374, 0.298, 0.012], [0.479, 0.415, 0.008]],
    soft: [0.222, 0.52, 0.04, 0.026, -0.5],
  },
};

var BF_FACE = {
  eyes: [[-0.17, -0.172], [0.13, -0.152]],
  e: 0.046,
  ey: 1.12,
  mouth: [-0.025, -0.128, 0.05],
  blush: [[-0.245, -0.11], [0.2, -0.06]],
  bw: 0.04,
  bh: 0.03,
};

/** A closed Catmull-Rom curve through unit points; a point [x, y, 1] is a sharp corner. */
function butterflyPath(pts, u) {
  return function (c) {
    var n = pts.length;
    c.moveTo(pts[0][0] * u, pts[0][1] * u);
    for (var i = 0; i < n; i++) {
      var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
      var k1 = p1[2] ? 0 : 1 / 6, k2 = p2[2] ? 0 : 1 / 6;
      c.bezierCurveTo(
        (p1[0] + (p2[0] - p0[0]) * k1) * u, (p1[1] + (p2[1] - p0[1]) * k1) * u,
        (p2[0] - (p3[0] - p1[0]) * k2) * u, (p2[1] - (p3[1] - p1[1]) * k2) * u,
        p2[0] * u, p2[1] * u
      );
    }
    c.closePath();
  };
}

/** Fine veins fanning out from the wing root (inside the wing's clip). */
function butterflyVeins(c, u, veins, color, w) {
  c.save();
  c.strokeStyle = color;
  c.lineWidth = w * u;
  c.lineCap = "round";
  for (var i = 0; i < veins.length; i++) {
    var v = veins[i];
    c.beginPath();
    c.moveTo(v[0] * u, v[1] * u);
    c.quadraticCurveTo(v[2] * u, v[3] * u, v[4] * u, v[5] * u);
    c.stroke();
  }
  c.restore();
}

/** The pale band just inside a wing's outline (the path stroked inside its own clip). */
function butterflyBand(c, path, color, w) {
  c.save();
  c.beginPath();
  path(c);
  c.strokeStyle = color;
  c.lineWidth = w;
  c.lineJoin = "round";
  c.stroke();
  c.restore();
}

/** Upper wing: pink with an orange glow at the root, veins, a pale pink band, gloss spots. */
function butterflyUpperWing(c, u, w) {
  var path = butterflyPath(w.pts, u);
  part(c, u, path, BF_UPPER, 0.42, w.c[0], w.c[1], {
    lightK: 0.72,
    rim: 0.2,
    inside: function (cc) {
      var g = cc.createRadialGradient(w.root[0] * u, w.root[1] * u, 0.04 * u, w.root[0] * u, w.root[1] * u, 0.58 * u);
      g.addColorStop(0, "rgba(255,166,40,1)");
      g.addColorStop(0.4, "rgba(255,158,52,0.92)");
      g.addColorStop(0.7, "rgba(255,138,120,0.4)");
      g.addColorStop(1, "rgba(255,120,170,0)");
      cc.fillStyle = g;
      cc.fillRect(-1.2 * u, -1.2 * u, 2.4 * u, 2.4 * u);
      butterflyVeins(cc, u, w.veins, "rgba(226,72,86,0.5)", 0.013);
      butterflyBand(cc, path, "#ffd8ec", 0.056 * u);
    },
  });
  butterflyGloss(c, u, w, "rgba(255,236,242,0.72)");
}

/** Lower wing: blue, deeper at the root, veins, a pale periwinkle band, a glassy spot. */
function butterflyLowerWing(c, u, w) {
  var path = butterflyPath(w.pts, u);
  part(c, u, path, BF_LOWER, 0.3, w.c[0], w.c[1], {
    lightK: 0.66,
    rim: 0.2,
    inside: function (cc) {
      var g = cc.createRadialGradient(w.root[0] * u, w.root[1] * u, 0.02 * u, w.root[0] * u, w.root[1] * u, 0.4 * u);
      g.addColorStop(0, "rgba(40,84,220,0.7)");
      g.addColorStop(1, "rgba(40,84,220,0)");
      cc.fillStyle = g;
      cc.fillRect(-1.2 * u, -1.2 * u, 2.4 * u, 2.4 * u);
      butterflyVeins(cc, u, w.veins, "rgba(36,62,190,0.6)", 0.013);
      butterflyBand(cc, path, "#a8c4ff", 0.064 * u);
      var s = w.soft;
      cc.fillStyle = "rgba(214,240,255,0.55)";
      cc.beginPath();
      cc.ellipse(s[0] * u, s[1] * u, s[2] * u, s[3] * u, s[4], 0, TAU);
      cc.fill();
    },
  });
  butterflyGloss(c, u, w, "rgba(222,246,255,0.85)");
}

/** The glassy spot (a pale pool with a white core) and the scattered white dots. */
function butterflyGloss(c, u, w, pool) {
  var s = w.spot;
  c.fillStyle = pool;
  c.beginPath();
  c.ellipse(s[0] * u, s[1] * u, s[2] * u, s[3] * u, s[4], 0, TAU);
  c.fill();
  gloss(c, u, s[0] - s[2] * 0.18, s[1] - s[3] * 0.25, s[2] * 0.55, s[3] * 0.5, s[4], 0.95);
  for (var i = 0; i < w.dots.length; i++) {
    var d = w.dots[i];
    gloss(c, u, d[0], d[1], d[2], d[2] * 0.9, 0, 0.88);
  }
}

/** A thin flat antenna: a brown line with a lighter core, bending from (x0, y0) to (x1, y1). */
function butterflyAntenna(c, u, x0, y0, qx, qy, x1, y1) {
  c.save();
  c.lineCap = "round";
  for (var k = 0; k < 2; k++) {
    c.strokeStyle = k ? BF_STALK_CORE : BF_STALK_INK;
    c.lineWidth = (k ? 0.011 : 0.03) * u;
    c.beginPath();
    c.moveTo(x0 * u, y0 * u);
    c.quadraticCurveTo(qx * u, qy * u, x1 * u, y1 * u);
    c.stroke();
  }
  c.restore();
}

export var butterfly = {
  disc: { base: "#ffc8a0", light: "#fff6c8", shade: "#cba2f4", ink: "#b77ad8" },
  discPattern: function (c, r) {
    // A pastel rainbow down the glass: sunny yellow on top, peach, pink, lilac at the bottom.
    var g = c.createLinearGradient(0, -r, 0, r);
    g.addColorStop(0, "rgba(255,206,40,0.85)");
    g.addColorStop(0.35, "rgba(255,190,110,0.55)");
    g.addColorStop(0.68, "rgba(255,150,200,0.6)");
    g.addColorStop(1, "rgba(150,130,255,0.85)");
    c.fillStyle = g;
    c.fillRect(-r, -r, 2 * r, 2 * r);
    bokeh(c, r, 63, 30, "#fffbe0", 0.3, 0.7);
    c.fillStyle = "rgba(255,255,255,0.85)";
    [[-0.55, 0.5, 0.05], [0.62, -0.48, 0.045], [0.5, 0.62, 0.04], [-0.66, -0.2, 0.035]].forEach(function (s) {
      c.beginPath();
      sparklePath(c, s[0] * r, s[1] * r, s[2] * r, 0);
      c.fill();
    });
  },
  draw: function (c, r, pose) {
    var u = r * BF_K;
    c.save();
    c.translate(BF_AT[0] * u, BF_AT[1] * u);
    // Back to front, as in the original: lower wings, the big upper wings over them, the
    // antennae, the body over the wing roots, the head.
    butterflyLowerWing(c, u, BF_WINGS.lowerL);
    butterflyLowerWing(c, u, BF_WINGS.lowerR);
    butterflyUpperWing(c, u, BF_WINGS.upperL);
    butterflyUpperWing(c, u, BF_WINGS.upperR);

    butterflyAntenna(c, u, -0.025, -0.33, -0.062, -0.522, -0.135, -0.645);
    butterflyAntenna(c, u, 0.1, -0.31, 0.146, -0.442, 0.215, -0.6);
    [[-0.15, -0.686], [0.234, -0.636]].forEach(function (k) {
      part(c, u, oval(k[0], k[1], 0.05, 0.05, 0, u), BF_KNOB, 0.05, k[0], k[1], { lightK: 0.55, line: 0.02 });
      gloss(c, u, k[0] - 0.012, k[1] - 0.016, 0.018, 0.012, -0.5);
    });

    var bx = -0.086, by = 0.335;
    part(c, u, oval(bx, by, 0.177, 0.235, 0, u), BF_BODY, 0.22, bx, by, {
      lightK: 0.62,
      inside: function (cc) {
        cc.strokeStyle = "rgba(214,86,20,0.8)";
        cc.lineWidth = 0.016 * u;
        cc.lineCap = "round";
        [[0.2, 0.16], [0.31, 0.175], [0.42, 0.15]].forEach(function (s) {
          cc.beginPath();
          cc.moveTo((bx - s[1]) * u, s[0] * u);
          cc.quadraticCurveTo(bx * u, (s[0] + 0.1) * u, (bx + s[1]) * u, s[0] * u);
          cc.stroke();
        });
      },
    });
    gloss(c, u, -0.17, 0.225, 0.03, 0.022, -0.4, 0.7);

    part(c, u, oval(-0.03, -0.12, 0.27, 0.25, 0, u), BF_HEAD, 0.26, -0.03, -0.12, { lightK: 0.66 });
    gloss(c, u, -0.12, -0.31, 0.06, 0.026, -0.3);
    gloss(c, u, -0.02, -0.34, 0.014, 0.012, 0);

    gardenFace(c, u, BF_FACE, pose);
    c.restore();
  },
};

// ---------------------------------------------------------------------------------------
// Tier 7 — Lizard: a lime gecko lying down facing left — one round head-and-body, yellow back
// spikes, the back running on into a tail rolled up in a spiral, legs bulging out below it.

var LZ_K = 0.94;
var LZ_SKIN = tone("#2f4f0c", "#a8cc2a", "#d9ef40", "#f1f96a", "#bedd34");
var LZ_BELLY = tone("#2f4f0c", "#efd27c", "#fdf6b4", "#fffbe2", "#f2e2a0");
var LZ_SPIKE = tone("#2f4f0c", "#f9a914", "#ffde30", "#fff27a", "#f8b820");
var LZ_SPOT = "rgba(146,186,34,0.85)";

// The silhouette, traced from the original (outline centre lines moved in by half the pen):
// one round head-and-body with no neck, a back sloping straight down into the tail, and the
// tail rolled up into a big round lobe that rises behind the end of the back. Clockwise from
// the notch where the back meets the lobe, which stays a sharp corner.
var LZ_NOTCH = [0.603, 0.005];
/** The lobe's edge up, over and down the right side, then the flat underside to the chin. */
var LZ_LOBE = [
  [0.592, -0.1], [0.607, -0.163], [0.642, -0.212], [0.697, -0.242], [0.764, -0.252],
  [0.83, -0.238], [0.884, -0.2], [0.92, -0.143], [0.94, -0.07], [0.945, 0.01], [0.935, 0.1],
  [0.91, 0.18], [0.868, 0.252], [0.808, 0.316], [0.73, 0.372], [0.635, 0.42], [0.52, 0.46],
  [0.39, 0.49], [0.22, 0.507], [0.0, 0.512], [-0.2, 0.512], [-0.35, 0.51], [-0.46, 0.502],
];
/** A chin sweeping up from the belly, then the round snout and forehead (no corners). */
var LZ_FRONT = [
  [-0.545, 0.486], [-0.6, 0.465], [-0.655, 0.437], [-0.7, 0.4], [-0.742, 0.355], [-0.785, 0.297],
  [-0.84, 0.25], [-0.885, 0.195], [-0.915, 0.13], [-0.933, 0.05], [-0.933, -0.04],
  [-0.913, -0.115], [-0.875, -0.185], [-0.823, -0.244], [-0.76, -0.302], [-0.693, -0.346],
];
/** The top of the head and the back, down to the notch. */
var LZ_BACK = [
  [-0.62, -0.381], [-0.54, -0.404], [-0.45, -0.416], [-0.35, -0.415], [-0.25, -0.403],
  [-0.15, -0.378], [-0.05, -0.337], [0.05, -0.29], [0.15, -0.24], [0.25, -0.188], [0.35, -0.131],
  [0.45, -0.073], [0.53, -0.033],
];
var LZ_OUTLINE = [LZ_NOTCH].concat(LZ_LOBE, LZ_FRONT, LZ_BACK);
/** Top edge of the cream belly: up under the chin, along the underside, up round the lobe. */
var LZ_BELLY_EDGE = [
  [-0.99, 0.06], [-0.92, 0.17], [-0.85, 0.24], [-0.75, 0.295], [-0.6, 0.335], [-0.4, 0.352],
  [-0.1, 0.362], [0.2, 0.368], [0.48, 0.362], [0.65, 0.335], [0.77, 0.29], [0.86, 0.215],
  [0.92, 0.12], [0.99, -0.02],
];
/** The back line carried on past the notch and wound into the tail's spiral (outline centre). */
var LZ_COIL = [
  [0.59, -0.012], [0.65, 0.012], [0.7, 0.021], [0.75, 0.012], [0.778, -0.012], [0.79, -0.045],
  [0.78, -0.08], [0.756, -0.108], [0.722, -0.12], [0.698, -0.106],
];
var LZ_COIL_END = [0.722, -0.062, 0.042];
/** Back spikes [front foot x, back foot x, tip x, tip y], head to tail, leaning tailward. */
var LZ_SPIKES = [
  [-0.557, -0.405, -0.47, -0.508], [-0.357, -0.213, -0.25, -0.52],
  [-0.147, -0.018, -0.045, -0.468], [0.013, 0.142, 0.11, -0.398], [0.173, 0.276, 0.24, -0.32],
  [0.313, 0.402, 0.365, -0.245], [0.433, 0.517, 0.485, -0.172], [0.53, 0.59, 0.56, -0.106],
];
/** Darker scales [x, y, rx, ry] down the back, tilted with it. */
var LZ_SPOTS = [
  [-0.14, -0.215, 0.026, 0.02], [-0.02, -0.175, 0.036, 0.026], [0.13, -0.183, 0.03, 0.022],
  [0.07, -0.11, 0.032, 0.024], [0.255, -0.09, 0.036, 0.026], [0.17, -0.04, 0.02, 0.016],
  [0.365, -0.05, 0.03, 0.022], [0.475, 0.012, 0.028, 0.019], [0.56, 0.037, 0.026, 0.017],
  [0.645, 0.073, 0.02, 0.014], [0.215, -0.185, 0.015, 0.012],
];
/** Pale dots [x, y, size] on the flank and the lobe. */
var LZ_DOTS = [
  [0.13, 0.05, 0.018], [0.27, 0.025, 0.015], [0.41, 0.055, 0.02], [0.67, 0.13, 0.019],
  [0.755, 0.115, 0.027], [0.835, 0.085, 0.016], [0.63, -0.17, 0.013], [0.86, -0.05, 0.014],
];
/**
 * Legs. The far front leg hangs behind the chin; the near front leg and the hind haunch bulge
 * out of the body in front of it, drawn like the original: a thigh with an outline only down
 * its sides (thin where it leaves the body) and two round toes pointing forward. thigh: a
 * closed curve clockwise from its top; left / right: outline spans as [from, to] point indices
 * of it (the taper is at the end that meets the body); toes [x, y, rx, ry], back toe first.
 */
var LZ_FAR_LEG = {
  thigh: [-0.77, 0.36, 0.07, 0.14],
  toes: [[-0.715, 0.49, 0.046, 0.032], [-0.79, 0.477, 0.042, 0.03]],
};
var LZ_NEAR_LEGS = [
  {
    thigh: [
      [0.42, 0.165], [0.52, 0.19], [0.585, 0.255], [0.607, 0.35], [0.585, 0.44], [0.525, 0.5],
      [0.44, 0.528], [0.345, 0.522], [0.275, 0.47], [0.255, 0.38], [0.272, 0.285], [0.33, 0.198],
    ],
    R: 0.17, cx: 0.43, cy: 0.35,
    right: [0.6, 6], left: [8, 11],
    toes: [[0.39, 0.5, 0.057, 0.042], [0.3, 0.488, 0.05, 0.04]],
    gloss: [0.35, 0.235, 0.06, 0.033, -0.25],
  },
  {
    thigh: [
      [-0.12, 0.29], [-0.05, 0.305], [-0.008, 0.355], [0.002, 0.435], [-0.025, 0.51],
      [-0.085, 0.556], [-0.16, 0.568], [-0.225, 0.535], [-0.248, 0.46], [-0.238, 0.385],
      [-0.195, 0.322],
    ],
    R: 0.13, cx: -0.12, cy: 0.43,
    right: [0.7, 5.5], left: [7.5, 10],
    toes: [[-0.16, 0.528, 0.055, 0.038], [-0.252, 0.512, 0.047, 0.036]],
    gloss: [-0.165, 0.37, 0.034, 0.023, -0.3],
  },
];
var LZ_FACE = {
  eyes: [[-0.71, -0.078], [-0.27, -0.06]],
  e: 0.07,
  ey: 1.04,
  mouth: [-0.497, -0.012, 0.075],
  blush: [[-0.82, 0.014], [-0.155, 0.036]],
  bw: 0.066,
  bh: 0.048,
};

/** Points along a closed smoothPath curve (the same Catmull-Rom beziers), `per` per span. */
function curveSamples(pts, per) {
  var out = [], n = pts.length;
  for (var i = 0; i < n; i++) {
    var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
    var c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    var c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    for (var k = 0; k < per; k++) {
      var t = k / per, s = 1 - t;
      out.push([
        s * s * s * p1[0] + 3 * s * s * t * c1[0] + 3 * s * t * t * c2[0] + t * t * t * p2[0],
        s * s * s * p1[1] + 3 * s * s * t * c1[1] + 3 * s * t * t * c2[1] + t * t * t * p2[1],
      ]);
    }
  }
  return out;
}

/** An open Catmull-Rom curve through unit points, its ends clamped (no wrap-around). */
function lizardCurve(c, pts, u) {
  var n = pts.length;
  c.moveTo(pts[0][0] * u, pts[0][1] * u);
  for (var i = 0; i < n - 1; i++) {
    var p0 = pts[Math.max(i - 1, 0)], p1 = pts[i], p2 = pts[i + 1];
    var p3 = pts[Math.min(i + 2, n - 1)];
    c.bezierCurveTo(
      (p1[0] + (p2[0] - p0[0]) / 6) * u, (p1[1] + (p2[1] - p0[1]) / 6) * u,
      (p2[0] - (p3[0] - p1[0]) / 6) * u, (p2[1] - (p3[1] - p1[1]) / 6) * u,
      p2[0] * u, p2[1] * u
    );
  }
}

/** The silhouette: round all the way except the notch, where the back meets the tail lobe. */
function lizardBody(u) {
  var pts = LZ_OUTLINE.concat([LZ_NOTCH]);
  return function (c) {
    lizardCurve(c, pts, u);
    c.closePath();
  };
}

/** The back's height at x (straight between the traced points; spike feet sink into the body). */
function lizardBackY(x) {
  var pts = LZ_BACK.concat([LZ_NOTCH]);
  for (var i = 1; i < pts.length; i++) {
    if (x <= pts[i][0] || i === pts.length - 1) {
      var a = pts[i - 1], b = pts[i], t = (x - a[0]) / (b[0] - a[0]);
      return a[1] + (b[1] - a[1]) * t;
    }
  }
  return pts[0][1];
}

/** One back spike: a bulging front edge up to a tip leaning toward the tail, a sheer back edge. */
function lizardSpikePath(s, u) {
  var fx = s[0], bx = s[1], tx = s[2], ty = s[3];
  var fy = lizardBackY(fx), by = lizardBackY(bx);
  return function (c) {
    c.moveTo(fx * u, (fy + 0.08) * u);
    c.lineTo(fx * u, fy * u);
    c.quadraticCurveTo((fx + (tx - fx) * 0.2) * u, (fy + (ty - fy) * 0.9) * u, tx * u, ty * u);
    c.quadraticCurveTo(bx * u, (ty + (by - ty) * 0.5) * u, bx * u, by * u);
    c.lineTo(bx * u, (by + 0.08) * u);
    c.closePath();
  };
}

function lizardSpike(c, u, s) {
  var fx = s[0], bx = s[1], tx = s[2], ty = s[3];
  var fy = lizardBackY(fx), by = lizardBackY(bx), h = (fy + by) / 2 - ty;
  part(c, u, lizardSpikePath(s, u), LZ_SPIKE, h * 0.75, (fx + bx) / 2, ty + h * 0.5, {
    line: 0.026,
    lightK: 0.4,
    rim: 0.2,
    inside: function (cc) {
      // The trailing face in orange, as in the original's two-tone spikes.
      cc.fillStyle = LZ_SPIKE.shade;
      cc.beginPath();
      cc.moveTo(tx * u, ty * u);
      cc.lineTo((bx + 0.03) * u, (by + 0.03) * u);
      cc.lineTo((fx + (bx - fx) * 0.15) * u, (fy + 0.02) * u);
      cc.closePath();
      cc.fill();
    },
  });
}

/**
 * An ink band along samples of a clockwise curve: its inner edge on the curve, its outer edge w
 * out, growing from nothing over the first (taper > 0) or last (taper < 0) part of its length.
 */
function lizardBand(c, u, s, w, taper) {
  var n = s.length, out = [];
  for (var i = 0; i < n; i++) {
    var a = s[Math.max(i - 1, 0)], b = s[Math.min(i + 1, n - 1)];
    var dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy) || 1;
    var t = taper > 0 ? i / (n - 1) : 1 - i / (n - 1);
    var k = Math.sin(Math.min(1, t / Math.abs(taper)) * Math.PI / 2);
    out.push([s[i][0] + (dy / d) * w * k, s[i][1] - (dx / d) * w * k]);
  }
  c.fillStyle = LZ_SKIN.ink;
  c.beginPath();
  c.moveTo(s[0][0] * u, s[0][1] * u);
  for (var j = 1; j < n; j++) c.lineTo(s[j][0] * u, s[j][1] * u);
  for (var k2 = n - 1; k2 >= 0; k2--) c.lineTo(out[k2][0] * u, out[k2][1] * u);
  c.closePath();
  c.fill();
}

/** Samples of a closed curve between two (fractional) point indices, wrapping round. */
function lizardSpan(pts, from, to) {
  var per = 10, all = curveSamples(pts, per), m = all.length, out = [];
  var i0 = Math.round(from * per), i1 = Math.round(to * per);
  if (i1 < i0) i1 += m;
  for (var i = i0; i <= i1; i++) out.push(all[i % m]);
  return out;
}

/** A round toe with its own outline. */
function lizardToe(c, u, t) {
  part(c, u, oval(t[0], t[1], t[2], t[3], 0, u), LZ_SKIN, t[2], t[0], t[1], {
    line: 0.02,
    lightK: 0.55,
    rim: 0.2,
  });
}

/** The far front leg: a plain outlined column with two toes, behind the chin. */
function lizardFarLeg(c, u, L) {
  var th = L.thigh;
  part(c, u, oval(th[0], th[1], th[2], th[3], 0, u), LZ_SKIN, th[2], th[0], th[1], {
    lightK: 0.6,
    rim: 0.2,
  });
  for (var i = 0; i < L.toes.length; i++) lizardToe(c, u, L.toes[i]);
}

/** A near leg bulging out of the body: shaded thigh, side outlines tapering into the body, toes. */
function lizardNearLeg(c, u, L) {
  candyPart(c, u, smooth(L.thigh, u), LZ_SKIN, L.R * u, {
    noOutline: true,
    cx: L.cx * u,
    cy: L.cy * u,
    lightK: 0.62,
    rim: 0.22,
  });
  lizardBand(c, u, lizardSpan(L.thigh, L.right[0], L.right[1]), LINE, 0.4);
  lizardBand(c, u, lizardSpan(L.thigh, L.left[0], L.left[1]), LINE, -0.4);
  var g = L.gloss;
  gloss(c, u, g[0], g[1], g[2], g[3], g[4], 0.85);
  gloss(c, u, g[0] + g[2] * 1.35, g[1] + g[3] * 0.5, g[3] * 0.38, g[3] * 0.38, 0, 0.7);
  for (var i = 0; i < L.toes.length; i++) lizardToe(c, u, L.toes[i]);
}

/** Cream belly, the darker ring round the spiral, scales and dots, then the coil itself. */
function lizardMarkings(c, u) {
  c.save();
  c.beginPath();
  lizardCurve(c, LZ_BELLY_EDGE, u);
  c.lineTo(1.1 * u, 0.7 * u);
  c.lineTo(-1.1 * u, 0.7 * u);
  c.closePath();
  c.clip();
  c.fillStyle = LZ_BELLY.base;
  c.fillRect(-1.1 * u, -0.2 * u, 2.2 * u, u);
  // A tan shade hugging the bottom outline.
  c.fillStyle = LZ_BELLY.shade;
  c.beginPath();
  lizardBody(u)(c);
  c.save();
  c.translate(-0.012 * u, -0.06 * u);
  lizardBody(u)(c);
  c.restore();
  c.fill("evenodd");
  c.restore();

  c.fillStyle = rgba(LZ_SKIN.shade, 0.5);
  c.beginPath();
  c.arc(0.738 * u, -0.066 * u, 0.1 * u, 0, TAU);
  c.fill();

  c.fillStyle = LZ_SPOT;
  for (var i = 0; i < LZ_SPOTS.length; i++) {
    var s = LZ_SPOTS[i];
    c.beginPath();
    c.ellipse(s[0] * u, s[1] * u, s[2] * u, s[3] * u, 0.5, 0, TAU);
    c.fill();
  }
  specks(c, u, LZ_DOTS, "rgba(255,255,214,0.55)");

  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = LZ_SKIN.ink;
  c.lineWidth = 0.036 * u;
  c.beginPath();
  lizardCurve(c, LZ_COIL, u);
  c.stroke();
  c.restore();
  c.fillStyle = LZ_SKIN.ink;
  c.beginPath();
  c.arc(LZ_COIL_END[0] * u, LZ_COIL_END[1] * u, LZ_COIL_END[2] * u, 0, TAU);
  c.fill();
}

export var lizard = {
  disc: { base: "#dcf050", light: "#fbffb0", shade: "#bcdc30", ink: "#a8c822" },
  discPattern: function (c, r) {
    bokeh(c, r, 74, 32, "#fbffd0", 0.25, 0.6);
  },
  draw: function (c, r, pose) {
    var u = r * LZ_K;
    // Behind the body: the far front leg and the back spikes.
    lizardFarLeg(c, u, LZ_FAR_LEG);
    for (var i = 0; i < LZ_SPIKES.length; i++) lizardSpike(c, u, LZ_SPIKES[i]);

    candyPart(c, u, lizardBody(u), LZ_SKIN, 0.38 * u, {
      line: LINE,
      swell: SWELL_K,
      rim: RIM,
      // The light: the head and the top of the body, and a ball of it round the coil.
      light: function (cc) {
        cc.ellipse(-0.33 * u, -0.1 * u, 0.6 * u, 0.32 * u, -0.12, 0, TAU);
        cc.moveTo(0.895 * u, -0.1 * u);
        cc.arc(0.735 * u, -0.1 * u, 0.16 * u, 0, TAU);
      },
      inside: function (cc) {
        lizardMarkings(cc, u);
      },
    });
    gloss(c, u, -0.66, -0.285, 0.055, 0.028, -0.38);
    gloss(c, u, -0.555, -0.335, 0.016, 0.013, 0);
    gloss(c, u, -0.505, -0.36, 0.011, 0.009, 0, 0.8);
    gloss(c, u, 0.73, -0.2, 0.04, 0.018, 0.05, 0.85);

    // In front: the hind haunch, then the near front leg.
    for (var j = 0; j < LZ_NEAR_LEGS.length; j++) lizardNearLeg(c, u, LZ_NEAR_LEGS[j]);

    gardenFace(c, u, LZ_FACE, pose);
  },
};

export var GARDEN_CREATURES = { snail: snail, bug: bug, butterfly: butterfly, lizard: lizard };
